
import { v4 as uuidv4 } from 'uuid';
import client from '../auth/client';
import type { LogoutType } from '../common/types';


interface PageVisit {
  pageName: string;
  lastVisited: string;
}

interface UserSession {
  userId: string;
  sessionId: string;
  loginTimestamp: string;
  loginTracked: boolean;
  logoutTimestamp?: string;
  pageVisits: PageVisit[];
}

class AuditService {
    private loginTrackingInProgress: boolean = false;
    //Track page visits in progress to prevent duplicates
    private pageVisitsInProgress = new Set<string>();

   /**
   * ✅ Generate or retrieve existing session ID
   */
  private getSessionId(): string {
    let sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      sessionId = uuidv4();                                   // Generate UUID like: "550e8400-e29b-41d4-a716-446655440000"
      sessionStorage.setItem('sessionId', sessionId);                                                     
      localStorage.setItem('lastSessionId', sessionId);
    } else {
      const lastSessionId = localStorage.getItem('lastSessionId');     
      if (lastSessionId !== sessionId) {                                     // Ensure localStorage is in sync
        localStorage.setItem('lastSessionId', sessionId);
      }
  }
    return sessionId;
  }
  
  /**
   * ✅ Check if this is a NEW session requiring LOGIN event
   * 
   * LOGIN event should ONLY be tracked when:
   * 1. User actually sees the login screen (authenticated from SSO)
   * 2. No previous session exists in localStorage (first time or after logout)
   * 
   * LOGIN event should NOT be tracked when:
   * 1. User reopens browser (JWT still valid, no login screen shown)
   * 2. Previous session exists in localStorage
   */
  isNewSession(): boolean {
    // ✅ STEP 1: Check if previous session exists in localStorage
    const lastSessionId = localStorage.getItem('lastSessionId');
    
    // ✅ STEP 2: Check active session in sessionStorage
    const sessionData = this.getSessionData();
    
    // ✅ SCENARIO 1: No localStorage backup = First login OR after logout
    if (!lastSessionId) {
      return true;
    }
    
    // SCENARIO 2: Session exists but not tracked yet
    if (sessionData && !sessionData.loginTracked) {
      return true;
    }
    
    // SCENARIO 3: Session exists and login was tracked
    if (sessionData && sessionData.loginTracked) {
      return false;
    }
    
    // SCENARIO 4: localStorage exists but no sessionStorage = Browser reopened
    return false;
  }

  /**
   * Get current session data from sessionStorage
   */
  getSessionData(): UserSession | null {
    const data = sessionStorage.getItem('userSession');
    return data ? JSON.parse(data) : null;
  }


   /**
   * Track Login Event
   * Should be called ONCE per session, BEFORE session is fully created
   */
  async trackLogin(userId: string) {
    //Already tracked?
    const existingSession = this.getSessionData();
    if (existingSession?.loginTracked) {
      return { status: 'skipped', message: 'Login already tracked' };
    }

    // Already in progress?
    if (this.loginTrackingInProgress) {
      return { status: 'skipped', message: 'Already in progress' };
    }

    //Check if there's an old session that wasn't properly closed
    const lastSessionId = localStorage.getItem('lastSessionId');
    if (lastSessionId) {
       try {
        await this.trackLogout(userId, lastSessionId);
      } catch (error) {
        console.error('Failed to track logout for previous session:', error);
      }
    }

    this.loginTrackingInProgress = true;
    const sessionId = this.getSessionId();
    const loginTimestamp = new Date().toISOString();
    
    // ✅ Store login timestamp in localStorage for session restoration
    localStorage.setItem('loginTimestamp', loginTimestamp);
    
    // ✨ Create session with loginTracked = false initially
    const sessionData: UserSession = {
      userId,
      sessionId,
      loginTimestamp,
      loginTracked: false,
      pageVisits: []
    };

    sessionStorage.setItem('userSession', JSON.stringify(sessionData));
    
    try {
      const response = await client.post("audit_log/login/", {
          session_id: sessionId,
          timestamp: loginTimestamp
        });

      const result = response.data;
      sessionData.loginTracked = true;
      sessionStorage.setItem('userSession', JSON.stringify(sessionData));
      return result;
      
    } catch (error) {
      console.error('❌ Failed to track login:', error);
      sessionData.loginTracked = true;
      sessionStorage.setItem('userSession', JSON.stringify(sessionData));
    } finally {
      this.loginTrackingInProgress = false;
    }
  }
   
    /**
   * Restore session from localStorage backup
   * Called when browser reopens with valid JWT but empty sessionStorage
   * MUST restore sessionId to sessionStorage BEFORE any other operations
   */
  restoreSessionFromBackup(userId: string) {
    const lastSessionId = localStorage.getItem('lastSessionId');
    const originalLoginTime = localStorage.getItem('loginTimestamp');
    
    if (!lastSessionId) {
      return false;
    }
    
    //Restore sessionId to sessionStorage FIRST
    sessionStorage.setItem('sessionId', lastSessionId);
    
    // ✅ Create session object with ORIGINAL login timestamp (not current time)
    const sessionData: UserSession = {
      userId,
      sessionId: lastSessionId,
      loginTimestamp: originalLoginTime || new Date().toISOString(), // Use original timestamp
      loginTracked: true,  // Mark as tracked
      pageVisits: []
    };
    
    // Restore to sessionStorage
    sessionStorage.setItem('userSession', JSON.stringify(sessionData));
    
    return true;
  }

   /**
   * Track Page Visit Event
   * Called when: User navigates to a screen
   * Important: This automatically increments visit count in backend
   */
  async trackPageVisit(pageName: string) {
    const sessionData = this.getSessionData();
    
    if (!sessionData) {
      return;
    }

      // Guard: Check if this page visit is already being tracked
    if (this.pageVisitsInProgress.has(pageName)) {
      return { status: 'skipped', message: 'Already in progress' };
    }

    // ✅ Mark this page visit as in progress
    this.pageVisitsInProgress.add(pageName);

    // Update local session data
    const existingPage = sessionData.pageVisits.find(
      p => p.pageName === pageName
    );
    
    if (existingPage) {
      existingPage.lastVisited = new Date().toISOString();
    } else {
      sessionData.pageVisits.push({
        pageName,
        lastVisited: new Date().toISOString()
      });
    }
    
     sessionStorage.setItem('userSession', JSON.stringify(sessionData));

    try {
      const response = await client.post("audit_log/page-visit/",{
          session_id: sessionData.sessionId,
          page_name: pageName,
          timestamp: new Date().toISOString()
      });

      const result = response.data;
      return result;
      
    } catch (error) {
      console.error('❌ Failed to track page visit:', error);
      return undefined
    }finally {
      // Use setTimeout to prevent immediate re-tracking (debounce effect)
      setTimeout(() => {
        this.pageVisitsInProgress.delete(pageName);
      }, 1000);
    }
  }


  /**
   * ✨ Track Logout Event
   * Called for: manual logout, token expiry
   * Uses fetch() to bypass axios interceptor and prevent infinite loops
   * @param userId - User ID (not used in current implementation but kept for consistency)
   * @param sessionId - Optional session ID. 
   * @param logoutType - Reason for logout: MANUAL | IDLE_TIMEOUT | TOKEN_EXPIRED | SESSION_EXPIRED
   */
  async trackLogout(userId: string, sessionId?: string, logoutType: LogoutType = 'MANUAL') {
    // Use provided sessionId or get from current session
    let targetSessionId: string | null = sessionId || null;
    
    if (!targetSessionId) {
      const sessionData = this.getSessionData();
      if (!sessionData) {
        console.warn('⚠️ No session data - clearing lastSessionId');
        localStorage.removeItem('lastSessionId');
        return;
      }
      targetSessionId = sessionData.sessionId;
    }

    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      };
      
      const apiUrl = `${process.env.REACT_APP_BACKEND_API_HOST}audit_log/logout/`;
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        keepalive: true, 
        body: JSON.stringify({
          session_id: targetSessionId,
          timestamp: new Date().toISOString(),
          logout_type: logoutType
        })
      });

      // ✅ Only clear lastSessionId if API call was successful
      if (response.ok && !sessionId) {
        localStorage.removeItem('lastSessionId');
      }

      if (!response.ok) {
      console.error(`❌ Logout API returned ${response.status}: ${response.statusText}`);
      }

    } catch (error) {
      console.error('❌ Failed to track logout:', error);
    }
  }
  
    /**
 *Track Browser Close Event
 * CRITICAL: This MUST use fetch() instead of axios/client because
 * - Ensures request completes even if page closes immediately
 */
  trackBrowserClose() {
    const sessionData = this.getSessionData();
    let sessionId: string | null = null;
    
    if (sessionData) {
      sessionId = sessionData.sessionId;
    } else {
      // ✅ Fallback: Get last known session ID from localStorage
      sessionId = localStorage.getItem('lastSessionId');
    }

    if (!sessionId) {
      console.warn('❌ No session ID available - cannot track browser close');
      return;
    }

    try {
      // Get auth token from localStorage (more reliable than sessionStorage during unload)
      const authToken = localStorage.getItem('ACCESS_TOKEN') || sessionStorage.getItem('ACCESS_TOKEN');
      
      if (!authToken) {
        console.warn('⚠️ No auth token - cannot track browser close');
        return;
      }

      // MUST use native fetch() - axios doesn't support keepalive
      const apiUrl = `${process.env.REACT_APP_BACKEND_API_HOST}audit_log/browser-close/`;
      
      console.log('📤 Sending browser close request to:', apiUrl);
      
      fetch(apiUrl, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        credentials: 'include',
        keepalive: true,  // MUST keep request alive even after page closes
        body: JSON.stringify({
          session_id: sessionId,
          timestamp: new Date().toISOString()
        })
      });   
    } catch (error: any) {
      console.error('❌ Failed to track browser close:', error.message);
    }
  }

 

}

export default new AuditService();